import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import TierChangeModal from "@/components/jea/TierChangeModal";
import RunQueryModal from "@/components/queries/RunQueryModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney, formatDate, TIERS } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const tierBadgeStyle = (tier) => {
  if (tier === "gold") return { background: "var(--color-tier-gold-bg-15)", border: "1px solid var(--color-tier-gold-border-40)", color: "var(--color-tier-gold)" };
  if (tier === "silver") return { background: "rgba(192, 192, 192, 0.15)", border: "1px solid rgba(192, 192, 192, 0.40)", color: "var(--color-tier-silver)" };
  return { background: "rgba(205, 127, 50, 0.15)", border: "1px solid rgba(205, 127, 50, 0.40)", color: "var(--color-tier-bronze)" };
};

const TierBadge = ({ tier, testid }) => {
  const info = TIERS.find((t) => t.key === tier) || TIERS[0];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]"
      style={tierBadgeStyle(tier)}
      data-testid={testid}
    >
      <Shield size={12} strokeWidth={2.5} />
      {info.label}
    </span>
  );
};

const SlotMeter = ({ used, max, onUpgrade }) => {
  const pct = max ? Math.min(100, (used / max) * 100) : 0;
  const full = max && used >= max;
  return (
    <JeaCard className="flex flex-col gap-2" data-testid="slot-meter">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-text-secondary">
          Judgment slots used: <span className="text-white font-semibold">{used} of {max}</span>
        </div>
        {full && (
          <button
            type="button"
            onClick={onUpgrade}
            className="text-[12px] font-semibold text-accent-pink hover:text-accent-pink-light border border-accent-pink px-3 h-8 rounded-[8px]"
            data-testid="slot-meter-upgrade"
          >
            Upgrade to PRO for unlimited judgments
          </button>
        )}
      </div>
      <div className="h-1.5 w-full rounded bg-border-default overflow-hidden">
        <motion.div
          className="h-full bg-accent-pink"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
    </JeaCard>
  );
};

const DebtorCard = ({ debtor, onRunQuery, onViewReports, onEditTier, onOpen, onRestore, canRunQueries, canMutateDebtors }) => {
  const isArchived = debtor.status === "archived";
  return (
  <JeaCard
    className={cn(
      "flex items-center gap-6 transition-colors",
      isArchived ? "opacity-70 hover:border-border-default" : "hover:border-accent-pink/40",
    )}
    data-testid={`debtor-card-${debtor.id}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onOpen}
          className="text-white font-bold text-[16px] hover:text-accent-pink transition-colors text-left"
          data-testid={`debtor-name-${debtor.id}`}
        >
          {debtor.full_name}
        </button>
        <TierBadge tier={debtor.tier} testid={`debtor-tier-${debtor.id}`} />
        {isArchived && (
          <span
            className="inline-flex px-2 py-[3px] rounded-full text-[10px] font-bold tracking-[0.08em] uppercase text-white"
            style={{ background: "#5A7A9A" }}
            data-testid={`debtor-archived-pill-${debtor.id}`}
          >
            Archived
          </span>
        )}
      </div>
      <div className="text-[13px] text-text-secondary mt-1">
        Case <span className="jea-caseno">{debtor.judgment?.case_number || "—"}</span>
      </div>
      <div className="grid grid-cols-3 gap-6 mt-3 text-[13px]">
        <div>
          <div className="jea-caption">Judgment</div>
          <div className="jea-mono text-accent-pink font-bold text-[15px]" data-testid={`debtor-amount-${debtor.id}`}>
            {formatMoney(debtor.judgment?.amount_cents || 0)}
          </div>
        </div>
        <div>
          <div className="jea-caption">Last Query</div>
          <div className="jea-date">{formatDate(debtor.last_query_at)}</div>
        </div>
        <div>
          <div className="jea-caption">Next Query</div>
          <div className="jea-date" data-testid={`debtor-next-query-${debtor.id}`}>
            {formatDate(debtor.next_query_at)}
          </div>
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-2 shrink-0">
      {isArchived ? (
        canMutateDebtors && (
          <JeaButton
            size="sm"
            variant="outline-pink"
            onClick={onRestore}
            data-testid={`debtor-restore-${debtor.id}`}
          >
            Restore
          </JeaButton>
        )
      ) : (
        canRunQueries && (
          <JeaButton size="sm" onClick={onRunQuery} data-testid={`debtor-run-query-${debtor.id}`}>
            Run Query
          </JeaButton>
        )
      )}
      <JeaButton
        size="sm"
        variant="outline-pink"
        onClick={onViewReports}
        data-testid={`debtor-view-reports-${debtor.id}`}
      >
        View Reports
      </JeaButton>
      {canMutateDebtors && (
        <JeaButton
          size="sm"
          variant="outline-neutral"
          onClick={onEditTier}
          data-testid={`debtor-edit-tier-${debtor.id}`}
        >
          Edit Tier
        </JeaButton>
      )}
    </div>
  </JeaCard>
);
};

const DebtorTable = ({ debtors, onOpen, onEditTier, onRunQuery, onViewReports, canRunQueries, canMutateDebtors }) => (
  <JeaCard className="p-0 overflow-hidden" data-testid="debtor-table">
    <table className="w-full">
      <thead>
        <tr className="text-left border-b border-border-default">
          {["Debtor Name", "Tier", "Next Query", "Last Query", "Judgment", "Status", "Actions"].map((h) => (
            <th key={h} className="jea-caption text-text-secondary p-4">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {debtors.map((d) => (
          <tr
            key={d.id}
            className="border-b border-border-default hover:bg-bg-tertiary transition-colors relative"
            data-testid={`debtor-row-${d.id}`}
          >
            <td className="p-4">
              <button
                type="button"
                onClick={() => onOpen(d)}
                className="text-white font-semibold hover:text-accent-pink transition-colors"
                data-testid={`debtor-name-${d.id}`}
              >
                {d.full_name}
              </button>
              <div className="text-[12px] jea-caseno">{d.judgment?.case_number || "—"}</div>
            </td>
            <td className="p-4"><TierBadge tier={d.tier} testid={`debtor-tier-${d.id}`} /></td>
            <td className="p-4 jea-date">{formatDate(d.next_query_at)}</td>
            <td className="p-4 jea-date">{formatDate(d.last_query_at)}</td>
            <td className="p-4 jea-mono text-accent-pink font-bold">{formatMoney(d.judgment?.amount_cents || 0)}</td>
            <td className="p-4">
              <span className={cn(
                "inline-flex px-2 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]",
                d.status === "active" && "bg-success/15 text-success",
                d.status === "attention_required" && "bg-warning/15 text-warning",
                d.status === "archived" && "bg-danger/15 text-danger"
              )}>
                {d.status.replace("_", " ").toUpperCase()}
              </span>
            </td>
            <td className="p-4">
              <div className="flex gap-1">
                {canRunQueries && (
                  <button
                    type="button"
                    onClick={() => onRunQuery(d)}
                    className="text-accent-pink hover:text-accent-pink-light text-[12px] font-semibold px-2"
                    data-testid={`debtor-run-query-${d.id}`}
                  >
                    Run Query
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onViewReports(d)}
                  className="text-text-secondary hover:text-white text-[12px] font-semibold px-2"
                  data-testid={`debtor-view-reports-${d.id}`}
                >
                  View Reports
                </button>
                {canMutateDebtors && (
                  <button
                    type="button"
                    onClick={() => onEditTier(d)}
                    className="text-text-secondary hover:text-white text-[12px] font-semibold px-2"
                    data-testid={`debtor-edit-tier-${d.id}`}
                  >
                    Edit Tier
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </JeaCard>
);

const MyDebtorsPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { inPreview, guardAction } = usePreviewGate();
  const { canMutateDebtors, canRunQueries } = useRolePermissions();
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotUsed, setSlotUsed] = useState(0);
  const [slotMax, setSlotMax] = useState(null);
  const [tierModal, setTierModal] = useState({ open: false, debtor: null, submitting: false });
  const [runQueryState, setRunQueryState] = useState({ open: false, debtor: null });
  const [showArchived, setShowArchived] = useState(false);
  const [allDebtors, setAllDebtors] = useState([]);

  const isStandard = user?.account_type === "standard";
  const atCap = isStandard && !inPreview && slotMax != null && slotUsed >= slotMax;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/debtors");
      setAllDebtors(data.debtors || []);
      const active = (data.debtors || []).filter((d) => d.status !== "archived");
      setDebtors(active);
      setSlotUsed(data.slot_used ?? active.length);
      setSlotMax(data.slot_max ?? null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const restore = async (d) => {
    try {
      await apiClient.patch(`/debtors/${d.id}`, { status: "active" });
      toast.success(`Restored ${d.full_name}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visibleDebtors = useMemo(() => {
    if (!showArchived) return debtors;
    return allDebtors;
  }, [debtors, allDebtors, showArchived]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = guardAction(
    () => {
      if (atCap) {
        toast.info("You've hit your Standard debtor cap — opening PRO upgrade.");
        nav("/billing?open=change-plan&target=pro-monthly");
        return;
      }
      nav("/debtors/new");
    },
    { feature: "adding debtors" }
  );

  const openTier = (d) => setTierModal({ open: true, debtor: d, submitting: false });
  const confirmTier = async (newTier) => {
    if (!tierModal.debtor) return;
    setTierModal((t) => ({ ...t, submitting: true }));
    try {
      await apiClient.patch(`/debtors/${tierModal.debtor.id}`, { tier: newTier });
      toast.success("Tier updated — next query date recomputed.");
      setTierModal({ open: false, debtor: null, submitting: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setTierModal((t) => ({ ...t, submitting: false }));
    }
  };

  const runQuery = (d) => setRunQueryState({ open: true, debtor: d });
  const viewReports = (d) => nav(`/debtors/${d.id}?tab=reports`);
  const onQueryDone = (result) => {
    setRunQueryState({ open: false, debtor: null });
    if (result?.query_run?.id && result?.query_run?.debtor_id) {
      nav(`/debtors/${result.query_run.debtor_id}/queries/${result.query_run.id}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-debtors-page">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="jea-caption">Enforcement</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">My Judgments</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isStandard && (
            <div className="text-[13px] text-text-secondary" data-testid="pro-debtor-count">
              Judgments: <span className="text-white font-semibold">{debtors.length}</span>
            </div>
          )}
          {canMutateDebtors && (
            <JeaButton
              onClick={handleAdd}
              disabled={atCap}
              data-testid="add-debtor-button"
              className="gap-2"
            >
              <Plus size={16} /> Add New Judgment
            </JeaButton>
          )}
        </div>
      </div>

      {isStandard && slotMax != null && (
        <SlotMeter used={slotUsed} max={slotMax} onUpgrade={() => nav("/billing?open=change-plan&target=pro-monthly")} />
      )}

      {isStandard && allDebtors.some((d) => d.status === "archived") && (
        <div className="flex items-center justify-end">
          <label className="inline-flex items-center gap-2 text-[13px] text-text-secondary cursor-pointer select-none"
                 data-testid="show-archived-toggle-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 accent-accent"
              style={{ accentColor: "var(--color-accent)" }}
              data-testid="show-archived-toggle"
            />
            Show archived judgments
          </label>
        </div>
      )}

      {loading ? (
        <JeaCard className="min-h-[240px] flex items-center justify-center">
          <div className="text-text-secondary text-[13px]">Loading judgments…</div>
        </JeaCard>
      ) : visibleDebtors.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No judgments yet"
          description={canMutateDebtors ? "Add your first judgment to start tracking enforcement." : "No judgments are visible for your role."}
          testid="debtors-empty-state"
          footer={
            canMutateDebtors ? (
              <JeaButton onClick={handleAdd} className="mt-2 gap-2" data-testid="empty-add-debtor">
                <Plus size={16} /> Add New Judgment
              </JeaButton>
            ) : null
          }
        />
      ) : isStandard ? (
        <div className="space-y-4">
          {visibleDebtors.map((d) => (
            <DebtorCard
              key={d.id}
              debtor={d}
              onOpen={() => nav(`/debtors/${d.id}`)}
              onRunQuery={() => runQuery(d)}
              onViewReports={() => viewReports(d)}
              onEditTier={() => openTier(d)}
              onRestore={() => restore(d)}
              canRunQueries={canRunQueries}
              canMutateDebtors={canMutateDebtors}
            />
          ))}
        </div>
      ) : (
        <DebtorTable
          debtors={visibleDebtors}
          onOpen={(d) => nav(`/debtors/${d.id}`)}
          onRunQuery={(d) => runQuery(d)}
          onViewReports={(d) => viewReports(d)}
          onEditTier={openTier}
          canRunQueries={canRunQueries}
          canMutateDebtors={canMutateDebtors}
        />
      )}

      <TierChangeModal
        open={tierModal.open}
        currentTier={tierModal.debtor?.tier}
        submitting={tierModal.submitting}
        onCancel={() => setTierModal({ open: false, debtor: null, submitting: false })}
        onConfirm={confirmTier}
      />

      <RunQueryModal
        open={runQueryState.open}
        debtor={runQueryState.debtor}
        onClose={() => setRunQueryState({ open: false, debtor: null })}
        onCompleted={onQueryDone}
      />
    </div>
  );
};

export default MyDebtorsPage;
